export type CoinDenomination = 1 | 5 | 10 | 25;

export interface CoinmakersVaultChallengeConfig {
  readonly id: string;
  readonly skillId: string;
  readonly prompt: string;
  readonly targetCents: number;
  readonly coinLimit: number;
  readonly availableCoins: readonly CoinDenomination[];
  readonly hints: readonly string[];
}

export function validateCoinmakersVaultChallenge<T extends CoinmakersVaultChallengeConfig>(config: T) {
  const validCoins: readonly number[] = [1, 5, 10, 25];
  if (!config.id.trim() || !config.skillId.trim() || !config.prompt.trim() || config.hints.length < 3) {
    throw new RangeError("Coinmaker's Vault requires identifiers, a prompt, and three hints.");
  }
  if (!Number.isInteger(config.targetCents) || config.targetCents < 1 || config.targetCents > 100
    || !Number.isInteger(config.coinLimit) || config.coinLimit < 1 || config.coinLimit > 12
    || config.availableCoins.length < 2
    || config.availableCoins.some(coin => !validCoins.includes(coin))) {
    throw new RangeError("Coinmaker's Vault values must use supported coins and a practical target.");
  }
  return config;
}

export const COINMAKERS_VAULT_CHALLENGES = [
  validateCoinmakersVaultChallenge({
    id: 'coinmakers-vault-17',
    skillId: 'math-coin-values-combinations',
    prompt: 'Power the copper press with exactly 17¢ using no more than 4 coins.',
    targetCents: 17,
    coinLimit: 4,
    availableCoins: [1, 5, 10, 25],
    hints: [
      'Start with the largest coin that does not pass 17¢.',
      'A 10¢ coin and a 5¢ coin make 15¢.',
      'Add two 1¢ coins to 15¢. That makes 17¢ with 4 coins.',
    ],
  }),
  validateCoinmakersVaultChallenge({
    id: 'coinmakers-vault-30',
    skillId: 'math-coin-values-combinations',
    prompt: 'Unlock the silver cabinet with exactly 30¢ using only 2 coins.',
    targetCents: 30,
    coinLimit: 2,
    availableCoins: [1, 5, 10, 25],
    hints: [
      'One coin can be worth 25¢.',
      'Find one more coin that adds 5¢ to 25¢.',
      '25¢ + 5¢ = 30¢.',
    ],
  }),
  validateCoinmakersVaultChallenge({
    id: 'coinmakers-vault-42',
    skillId: 'math-solve-money-problems',
    prompt: 'Repair the teal mint with exactly 42¢ using no more than 5 coins.',
    targetCents: 42,
    coinLimit: 5,
    availableCoins: [1, 5, 10, 25],
    hints: [
      'Use 25¢ first, then build the remaining 17¢.',
      '25¢ + 10¢ + 5¢ makes 40¢.',
      'Add two 1¢ coins to 40¢. The five coins total 42¢.',
    ],
  }),
  validateCoinmakersVaultChallenge({
    id: 'coinmakers-vault-65',
    skillId: 'math-solve-money-problems',
    prompt: 'Open the royal vault with exactly 65¢ using no more than 4 coins.',
    targetCents: 65,
    coinLimit: 4,
    availableCoins: [1, 5, 10, 25],
    hints: [
      'Two 25¢ coins make 50¢.',
      'The amount from 50¢ to 65¢ is 15¢.',
      'Add 10¢ and 5¢ to the two 25¢ coins. Four coins make 65¢.',
    ],
  }),
] as const satisfies readonly CoinmakersVaultChallengeConfig[];

export const COINMAKERS_VAULT_COMPLETION_EVENT = 'coinmakers-vault-opened';
